import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Alert, Button, Card, Space, Table, Tabs, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useUser } from '../../contexts/UserContext';
import {
  createFitfinderSession,
  listFitfinderSessions,
  listProductSolutions,
  transitionProductSolution,
  type FitfinderSession,
  type ProductSolutionEntry,
} from '../../services/api/fitfinderService';
import { getApiErrorMessage } from '../../utils/apiErrors';
import { resolveWorkspaceCompanyId } from '../../utils/resolveWorkspaceCompanyId';

const { Title, Paragraph } = Typography;

/** Ant Design `Table` pagination uses 1-based page indices. */
const INITIAL_SESSIONS_PAGE = 1;

/** Default `limit` for `GET /fitfinder/sessions` on first load; must appear in `SESSIONS_PAGE_SIZE_OPTIONS`. */
const INITIAL_SESSIONS_PAGE_SIZE = 20;

const SESSIONS_PAGE_SIZE_OPTIONS = ['10', '20', '50'] as const;

const FITFINDER_CATALOG_TAB_KEY = 'catalog';
const FITFINDER_SESSIONS_TAB_KEY = 'sessions';

/**
 * Partner-facing FitFinder hub: **listProductSolutions** (all states + publish), not only
 * **getFitfinderCatalog** (ACTIVE + FitFinder-enabled). Sessions tab uses **listFitfinderSessions**.
 */
export const FitfinderCatalogPage: React.FC = () => {
  const navigate = useNavigate();
  const {
    user,
    selectedCompany,
    availableCompanies: companiesList = [],
    loadUserCompanies,
    isAuthenticated,
  } = useUser();
  const companyId = useMemo(
    () => resolveWorkspaceCompanyId(user?.companyId, selectedCompany?.id, companiesList),
    [user?.companyId, selectedCompany?.id, companiesList]
  );

  const [rows, setRows] = useState<ProductSolutionEntry[]>([]);
  const [sessions, setSessions] = useState<FitfinderSession[]>([]);
  const [loading, setLoading] = useState(false);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [startingId, setStartingId] = useState<string | null>(null);
  const [publishingId, setPublishingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [sessionsError, setSessionsError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<string>(FITFINDER_CATALOG_TAB_KEY);
  const [sessionsPage, setSessionsPage] = useState(INITIAL_SESSIONS_PAGE);
  const [sessionsPageSize, setSessionsPageSize] = useState(INITIAL_SESSIONS_PAGE_SIZE);

  useEffect(() => {
    if (!user?.id || !isAuthenticated) return;
    void loadUserCompanies();
  }, [user?.id, isAuthenticated, loadUserCompanies]);

  useEffect(() => {
    if (!companyId) {
      setRows([]);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    void listProductSolutions({ company_id: companyId, include_inactive: true })
      .then((list) => {
        if (!cancelled) setRows(list ?? []);
      })
      .catch((e: unknown) => {
        if (!cancelled) setError(getApiErrorMessage(e, 'Could not load catalog.'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [companyId]);

  useEffect(() => {
    setSessionsPage(INITIAL_SESSIONS_PAGE);
  }, [companyId]);

  useEffect(() => {
    if (!companyId || activeTab !== FITFINDER_SESSIONS_TAB_KEY) {
      return;
    }
    let cancelled = false;
    setSessionsLoading(true);
    setSessionsError(null);
    const offset = (sessionsPage - 1) * sessionsPageSize;
    void listFitfinderSessions({
      company_id: companyId,
      limit: sessionsPageSize,
      offset,
    })
      .then((res) => {
        if (!cancelled) setSessions(res.sessions ?? []);
      })
      .catch((e: unknown) => {
        if (!cancelled) setSessionsError(getApiErrorMessage(e, 'Could not load sessions.'));
      })
      .finally(() => {
        if (!cancelled) setSessionsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [companyId, activeTab, sessionsPage, sessionsPageSize]);

  const sessionsMayHaveMore = sessions.length === sessionsPageSize;
  const sessionsPaginationTotal =
    (sessionsPage - 1) * sessionsPageSize + sessions.length + (sessionsMayHaveMore ? 1 : 0);

  const startSession = async (solutionId: string) => {
    setStartingId(solutionId);
    setError(null);
    try {
      const session = await createFitfinderSession({
        solution_artifact_item_id: solutionId,
      });
      navigate(`/fitfinder/session/${session.id}`, { replace: false });
    } catch (e: unknown) {
      setError(getApiErrorMessage(e, 'Could not create FitFinder session.'));
    } finally {
      setStartingId(null);
    }
  };

  const publishSolution = async (artifactItemId: string) => {
    setPublishingId(artifactItemId);
    setError(null);
    try {
      await transitionProductSolution(artifactItemId, 'ACTIVE');
      const list = await listProductSolutions({ company_id: companyId!, include_inactive: true });
      setRows(list ?? []);
    } catch (e: unknown) {
      setError(getApiErrorMessage(e, 'Could not publish solution.'));
    } finally {
      setPublishingId(null);
    }
  };

  const catalogColumns: ColumnsType<ProductSolutionEntry> = [
    {
      title: 'Solution',
      dataIndex: 'solution_id',
      key: 'solution_id',
      ellipsis: true,
      render: (id: string) => <Typography.Text code>{id}</Typography.Text>,
    },
    {
      title: 'Workflow state',
      dataIndex: 'state',
      key: 'state',
      width: 120,
      render: (v: string | undefined) => v ?? '—',
    },
    {
      title: 'FitFinder enabled',
      dataIndex: 'fitfinder_enabled',
      key: 'fitfinder_enabled',
      width: 130,
      render: (v: boolean | undefined) => (v ? 'Yes' : 'No'),
    },
    {
      title: 'Match disposition',
      dataIndex: 'match_disposition',
      key: 'match_disposition',
      width: 160,
      render: (v: string | undefined) => v ?? '—',
    },
    {
      title: 'Situation type',
      dataIndex: 'situation_type',
      key: 'situation_type',
      width: 140,
      render: (v: string | undefined) => v ?? '—',
    },
    {
      title: '',
      key: 'actions',
      width: 280,
      render: (_: unknown, record: ProductSolutionEntry) => {
        const st = String(record.state ?? '').toUpperCase();
        const canStart = st === 'ACTIVE' && record.fitfinder_enabled === true;
        const canPublish = st === 'INITIALIZED';
        return (
          <Space size="small" wrap>
            {canPublish && (
              <Button
                type="primary"
                size="small"
                loading={publishingId === record.solution_id}
                onClick={() => void publishSolution(record.solution_id)}
              >
                Publish to catalog
              </Button>
            )}
            {canStart && (
              <Button
                type="default"
                size="small"
                loading={startingId === record.solution_id}
                onClick={() => void startSession(record.solution_id)}
              >
                Start session
              </Button>
            )}
            {st === 'ACTIVE' && !record.fitfinder_enabled && (
              <Typography.Text type="secondary">Enable FitFinder on this solution to list it.</Typography.Text>
            )}
            {st === 'CLOSED' && <Typography.Text type="secondary">Closed</Typography.Text>}
          </Space>
        );
      },
    },
  ];

  const sessionColumns: ColumnsType<FitfinderSession> = [
    {
      title: 'Session',
      dataIndex: 'id',
      key: 'id',
      ellipsis: true,
      render: (id: string) => (
        <Link to={`/fitfinder/session/${id}`}>
          <Typography.Text code>{id}</Typography.Text>
        </Link>
      ),
    },
    { title: 'State', dataIndex: 'state', key: 'state', width: 120 },
    {
      title: 'Updated',
      dataIndex: 'updated_at',
      key: 'updated_at',
      width: 200,
      render: (v: string | null | undefined) => v ?? '—',
    },
  ];

  return (
    <div style={{ maxWidth: 960, margin: '40px auto', padding: 24 }}>
      <Card>
        <Title level={3} style={{ marginTop: 0 }}>
          FitFinder catalog
        </Title>
        <Paragraph type="secondary">
          Product solutions for your workspace company: publish (INITIALIZED → ACTIVE) to make a solution
          eligible for the public catalog when FitFinder is enabled, then start a prospect session. Recent
          sessions are listed in the Sessions tab.
        </Paragraph>
        {!companyId && (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
            message="Company required"
            description="Select a workspace company in Control Bridge, then return here."
          />
        )}
        {error && <Alert type="error" showIcon style={{ marginBottom: 16 }} message={error} />}
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={[
            {
              key: FITFINDER_CATALOG_TAB_KEY,
              label: 'Solutions',
              children: (
                <Table<ProductSolutionEntry>
                  rowKey="solution_id"
                  loading={loading}
                  columns={catalogColumns}
                  dataSource={rows}
                  pagination={false}
                  locale={{ emptyText: companyId ? 'No product solutions for this company.' : '—' }}
                />
              ),
            },
            {
              key: FITFINDER_SESSIONS_TAB_KEY,
              label: 'Sessions',
              children: (
                <>
                  {sessionsError && (
                    <Alert type="error" showIcon style={{ marginBottom: 16 }} message={sessionsError} />
                  )}
                  <Table<FitfinderSession>
                    rowKey="id"
                    loading={sessionsLoading}
                    columns={sessionColumns}
                    dataSource={sessions}
                    pagination={{
                      current: sessionsPage,
                      pageSize: sessionsPageSize,
                      total: sessionsPaginationTotal,
                      showSizeChanger: true,
                      pageSizeOptions: [...SESSIONS_PAGE_SIZE_OPTIONS],
                      onChange: (page, size) => {
                        setSessionsPageSize(size);
                        setSessionsPage(
                          size !== sessionsPageSize ? INITIAL_SESSIONS_PAGE : page
                        );
                      },
                    }}
                    locale={{ emptyText: companyId ? 'No sessions for this company.' : '—' }}
                  />
                </>
              ),
            },
          ]}
        />
        <Space style={{ marginTop: 16 }}>
          <Button type="link" onClick={() => navigate(-1)} style={{ paddingLeft: 0 }}>
            Back
          </Button>
        </Space>
      </Card>
    </div>
  );
};
